import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SyncDryRun, SyncRunSummary } from '@nemo/api';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';
import { isRunning } from './format.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

/** While a run is queued or running the page asks again every five seconds. */
const POLL_MS = 5_000;

export interface Affinity {
  lastRun: SyncRunSummary | null | undefined;
  /** Starts a run, after a confirm that says what it will spend. */
  start: () => void;
  isStarting: boolean;
  /** Reads everything and writes nothing, and hands back what it would do. */
  dryRun: () => void;
  isDryRunning: boolean;
  /** The last plan, until somebody asks for another one. */
  plan: SyncDryRun | null;
  dismissPlan: () => void;
}

export function useAffinity(): Affinity {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const lastRun = useQuery(
    trpc.affinity.lastRun.queryOptions(undefined, {
      refetchInterval: (query) => (isRunning(query.state.data) ? POLL_MS : false),
    }),
  );

  const [plan, setPlan] = useState<SyncDryRun | null>(null);

  const sync = useMutation(
    trpc.affinity.sync.mutationOptions({
      onSuccess: () => {
        toast('Syncing Affinity…', { tone: 'info' });
        void queryClient.invalidateQueries({ queryKey: trpc.affinity.lastRun.queryKey() });
      },
      onError: (error) => toast(error.message),
    }),
  );

  // A dry run writes nothing, so it claims no live slot and invalidates no
  // query: the only thing it changes is what the drawer has to show.
  const dry = useMutation(
    trpc.affinity.sync.mutationOptions({
      onSuccess: (result) => setPlan(result.dryRun),
      onError: (error) => toast(error.message),
    }),
  );

  // A run writes relationships and people, so the grid is stale the moment it
  // finishes rather than the moment it starts.
  const lastFinishedAt = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastRun.isPending) return;
    const finishedAt = lastRun.data?.finishedAt ?? null;
    // The first answer is a run that ended before this page opened, which the
    // rows on it already show: refetching for it would load the grid twice.
    const first = lastFinishedAt.current === undefined;
    if (finishedAt === lastFinishedAt.current) return;
    lastFinishedAt.current = finishedAt;
    if (first || finishedAt === null) return;
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.investors.list.queryKey() });
  }, [lastRun.isPending, lastRun.data?.finishedAt, queryClient, trpc]);

  /**
   * The number first: what the run is projected to spend, against what is
   * left of the month. A run over the floor is refused before it is offered;
   * a dry run reads as much as a real one, so it is asked the same way.
   */
  const confirmCalls = useCallback(
    async (what: string): Promise<{ confirmedCalls: number } | null> => {
      let runPlan;
      try {
        runPlan = await queryClient.fetchQuery(trpc.affinity.plan.queryOptions({}));
      } catch (error) {
        toast(error instanceof Error ? error.message : String(error));
        return null;
      }
      const calls = Math.min(runPlan.projectedCalls, runPlan.ceiling);
      const left =
        runPlan.quotaRemaining === null
          ? ''
          : ` Affinity has ${runPlan.quotaRemaining.toLocaleString('en-GB')} calls left this month.`;
      if (runPlan.breachesFloor) {
        toast(
          `${what} would make about ${calls.toLocaleString('en-GB')} Affinity calls and take the month under its reserve of ${runPlan.quotaReserve.toLocaleString('en-GB')}.${left} Not started.`,
        );
        return null;
      }
      const question = `${what}: about ${calls.toLocaleString('en-GB')} Affinity calls.${left} Start?`;
      if (!window.confirm(question)) return null;
      return { confirmedCalls: calls };
    },
    [queryClient, toast, trpc],
  );

  const start = useCallback(() => {
    void confirmCalls('Sync relationship strengths from Affinity').then((confirmed) => {
      if (confirmed !== null) sync.mutate({ dryRun: false, ...confirmed });
    });
  }, [confirmCalls, sync]);

  const dryRun = useCallback(() => {
    setPlan(null);
    void confirmCalls('A dry run reads everything a sync would').then((confirmed) => {
      if (confirmed !== null) dry.mutate({ dryRun: true, ...confirmed });
    });
  }, [confirmCalls, dry]);

  const dismissPlan = useCallback(() => setPlan(null), []);

  return {
    lastRun: lastRun.data,
    start,
    isStarting: sync.isPending,
    dryRun,
    isDryRunning: dry.isPending,
    plan,
    dismissPlan,
  };
}
